/**
 * Today on the Swiss calendar, as a SQL date expression.
 *
 * `current_date` and `to_char(now(), ...)` give the day in the database session's time zone,
 * which nothing sets to Europe/Zurich (UTC on the Azure server), so between midnight and
 * 01:00/02:00 in Zurich they name yesterday. A date a person reads (an Antrag date, a Frist,
 * a dossier's creation date) or a Frist compared against today uses this instead.
 *
 * `now() AT TIME ZONE <zone>` gives the wall clock in that zone whatever the session's time
 * zone is, and the cast keeps its calendar day; Postgres resolves DST from the zone.
 */
import { MAHNWESEN_TZ } from './mandate';

export const ZURICH_TODAY_SQL = `(now() AT TIME ZONE '${MAHNWESEN_TZ}')::date`;

/** `ZURICH_TODAY_SQL` as `YYYY-MM-DD` text, for the "today" readers. */
export const ZURICH_TODAY_ISO_SQL = `to_char(${ZURICH_TODAY_SQL}, 'YYYY-MM-DD')`;

/** Today on the Swiss calendar as `YYYY-MM-DD`, for code that needs it outside SQL. */
export function zurichTodayIso(now: Date = new Date()): string {
  // Built from the parts, not from a locale's output shape: no locale promises YYYY-MM-DD.
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: MAHNWESEN_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(now);
  const part = (type: 'year' | 'month' | 'day'): string =>
    parts.find((p) => p.type === type)?.value ?? '';
  return `${part('year')}-${part('month')}-${part('day')}`;
}

/** A timestamp as `dd.mm.yyyy` on the Swiss calendar day; null for a value that is no timestamp. */
export function formatZurichDateDe(value: Date | string): string | null {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat('de-CH', {
    timeZone: MAHNWESEN_TZ,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(date);
}

/** The Swiss calendar day of an instant (ISO timestamp, `YYYY-MM-DD` or Date) as `YYYY-MM-DD`. */
export function zurichDateOf(instant: Date | string): string {
  return zurichTodayIso(new Date(instant));
}

/** The offset of Europe/Zurich from UTC at an instant, in minutes (60 in winter, 120 in summer). */
function zurichOffsetMinutes(ms: number): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: MAHNWESEN_TZ,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(new Date(ms));
  const n = (type: Intl.DateTimeFormatPartTypes): number => Number(parts.find((p) => p.type === type)?.value ?? 0);
  const wall = Date.UTC(n('year'), n('month') - 1, n('day'), n('hour'), n('minute'), n('second'));
  return Math.round((wall - Math.floor(ms / 1000) * 1000) / 60_000);
}

/**
 * The last millisecond of a Swiss calendar day (`YYYY-MM-DD`, 23:59:59.999 in Europe/Zurich) as an ISO
 * instant. DST-correct: the offset is the one in force at that wall-clock time on that day.
 */
export function zurichEndOfDay(isoDate: string): string {
  const [y, m, d] = isoDate.slice(0, 10).split('-').map((s) => parseInt(s, 10));
  const wallMs = Date.UTC(y!, m! - 1, d!, 23, 59, 59, 999);
  // First guess with the offset at the wall time read as UTC, then once more at the found instant
  // (the two differ only within the hours around a DST change, never at 23:59 local).
  let instant = wallMs - zurichOffsetMinutes(wallMs) * 60_000;
  instant = wallMs - zurichOffsetMinutes(instant) * 60_000;
  return new Date(instant).toISOString();
}
